import React from 'react';
import { Box, Typography, Button, TextField, InputAdornment, Select, MenuItem, Avatar, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Dialog, DialogContent, DialogTitle, Backdrop, Divider, Menu, ListItemIcon, ListItemText, Chip, Drawer, Grid, CircularProgress } from '@mui/material';
import { Search, Visibility, Close, DragIndicator, MoreVert, ArrowUpward, ArrowDownward, UnfoldMore } from '@mui/icons-material';
import EditIcon from '../components/icons/EditIcon';
import { useAuth } from '../contexts/AuthContext';
import userService, { AppUser } from '../services/userService';
import bugReportService, { BugReport, BugReportHistoryItem, BugReportComment } from '../services/bugReportService';
import AddBugReportForm from '../components/bugReport/AddBugReportForm';

const BugReportPage: React.FC = () => {
  const { user } = useAuth();
  const [searchText, setSearchText] = React.useState('');
  const [manager, setManager] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState<'' | 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa'>('');
  const [users, setUsers] = React.useState<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url'>[]>([]);
  const [sortColumn, setSortColumn] = React.useState<string | null>(null);
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [formOpen, setFormOpen] = React.useState(false);
  const [bugReports, setBugReports] = React.useState<BugReport[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [selectedBugReport, setSelectedBugReport] = React.useState<BugReport | null>(null);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [enlargedImageUrl, setEnlargedImageUrl] = React.useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = React.useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = React.useState<BugReport | null>(null);
  const [editOpen, setEditOpen] = React.useState(false);
  const [history, setHistory] = React.useState<BugReportHistoryItem[]>([]);
  const [comments, setComments] = React.useState<BugReportComment[]>([]);
  const [loadingHistory, setLoadingHistory] = React.useState(false);
  const [newComment, setNewComment] = React.useState('');
  const [addingComment, setAddingComment] = React.useState(false);
  const tableHeight = 432;

  // Kolumny: tryb zmiany szerokości + zapamiętywanie per użytkownik
  const [colResizeMode, setColResizeMode] = React.useState<boolean>(false);
  const [dragCol, setDragCol] = React.useState<{ key: string; startX: number; startW: number } | null>(null);
  const defaultColumnWidths = React.useMemo<Record<string, number>>(() => ({
    id: 60,
    date: 100,
    title: 250,
    reporter: 180,
    status: 120,
    preview: 60,
    actions: 60,
  }), []);
  const STORAGE_KEY = React.useMemo(() => `bugReportsTableColWidths:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnWidths, setColumnWidths] = React.useState<Record<string, number>>(defaultColumnWidths);
  const getColW = (key: string) => `${columnWidths[key] ?? defaultColumnWidths[key] ?? 40}px`;

  // Dostosuj kolumny – widoczność
  const DEFAULT_VIS: Record<string, boolean> = React.useMemo(() => ({
    id: true, date: true, title: true, reporter: true, status: true, preview: true, actions: true
  }), []);
  const VISIBLE_KEY = React.useMemo(() => `bugReports.columns.visible:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnsVisible, setColumnsVisible] = React.useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem(VISIBLE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return { ...DEFAULT_VIS, ...parsed };
      }
    } catch {}
    return { ...DEFAULT_VIS };
  });
  const colV = React.useCallback((key: string) => columnsVisible[key] !== false, [columnsVisible]);
  
  // Kolejność kolumn
  const COLUMNS_ORDER_DEFAULT: string[] = ['id', 'date', 'title', 'reporter', 'status', 'preview', 'actions'];
  const ORDER_KEY = React.useMemo(() => `bugReports.columns.order:${user?.user_id || 'anon'}`, [user?.user_id]);
  const [columnOrder, setColumnOrder] = React.useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(ORDER_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const missing = COLUMNS_ORDER_DEFAULT.filter(k => !parsed.includes(k));
        return [...parsed, ...missing];
      }
    } catch {}
    return [...COLUMNS_ORDER_DEFAULT];
  });
  
  // Panel kolumn
  const [colsAnchorEl, setColsAnchorEl] = React.useState<null | HTMLElement>(null);
  const colsOpen = Boolean(colsAnchorEl);
  const [draftColumnsVisible, setDraftColumnsVisible] = React.useState<Record<string, boolean> | null>(null);
  const [draftOrder, setDraftOrder] = React.useState<string[] | null>(null);

  // Inicjalizuj draft wartości gdy okno się otwiera
  React.useEffect(() => {
    if (colsOpen) {
      if (draftColumnsVisible === null) {
        setDraftColumnsVisible({ ...columnsVisible });
      }
      if (draftOrder === null) {
        setDraftOrder([...columnOrder]);
      }
    } else {
      // Resetuj draft wartości gdy okno się zamyka
      setDraftColumnsVisible(null);
      setDraftOrder(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colsOpen]); // Inicjalizuj tylko gdy okno się otwiera/zamyka

  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') setColumnWidths((prev) => ({ ...prev, ...parsed }));
      }
    } catch {}
  }, [STORAGE_KEY]);

  React.useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(columnWidths)); } catch {}
  }, [STORAGE_KEY, columnWidths]);

  React.useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragCol) return;
      const dx = e.clientX - dragCol.startX;
      const newW = Math.max(16, dragCol.startW + dx);
      setColumnWidths((prev) => ({ ...prev, [dragCol.key]: newW }));
    };
    const onUp = () => setDragCol(null);
    if (dragCol) {
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp, { once: true } as any);
    }
    return () => {
      window.removeEventListener('mousemove', onMove);
    };
  }, [dragCol]);

  const renderResizableTh = (keyId: string, node: React.ReactNode, align?: 'left'|'right'|'center', sortable: boolean = true) => (
    <TableCell 
      align={align} 
      sx={{ 
        whiteSpace: 'nowrap', 
        overflow: 'hidden', 
        textOverflow: 'ellipsis', 
        padding: '6px 12px', 
        fontSize: '0.875rem', 
        width: getColW(keyId), 
        maxWidth: 'none', 
        position: 'relative', 
        userSelect: 'none', 
        display: colV(keyId) ? 'table-cell' : 'none',
        cursor: sortable && (keyId === 'id' || keyId === 'date' || keyId === 'title' || keyId === 'reporter' || keyId === 'status') ? 'pointer' : 'default'
      }}
      onClick={sortable && (keyId === 'id' || keyId === 'date' || keyId === 'title' || keyId === 'reporter' || keyId === 'status') ? () => handleSort(keyId) : undefined}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
        {node}
        {sortable && (keyId === 'id' || keyId === 'date' || keyId === 'title' || keyId === 'reporter' || keyId === 'status') && renderSortIcon(keyId)}
      </Box>
      {colResizeMode ? (
        <Box
          role="separator"
          aria-orientation="vertical"
          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setDragCol({ key: keyId, startX: e.clientX, startW: columnWidths[keyId] ?? defaultColumnWidths[keyId] ?? 40 }); }}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            width: '12px',
            height: '100%',
            cursor: 'col-resize',
            zIndex: 6,
            touchAction: 'none',
            backgroundImage: 'linear-gradient(to right, transparent 45%, #FF0000 45%, #FF0000 55%, transparent 55%)',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.06)' }
          }}
        />
      ) : null}
    </TableCell>
  );

  React.useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const list = await userService.listUsersPublic();
        if (mounted) setUsers(list);
      } catch {}
    })();
    return () => { mounted = false; };
  }, []);

  const loadBugReports = React.useCallback(async () => {
    setLoading(true);
    try {
      const reporterId = manager && manager !== '' ? users.find(u => (u.display_name || u.email || u.user_id) === manager)?.user_id : undefined;
      const reports = await bugReportService.listBugReports({
        reporter_user_id: reporterId,
        status: statusFilter ? (statusFilter as 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa') : undefined,
        search: searchText || undefined
      });
      
      setBugReports(reports || []);
      return reports || [];
    } catch (err) {
      console.error('Błąd podczas ładowania zgłoszeń:', err);
      setBugReports([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [manager, statusFilter, searchText, users]);

  React.useEffect(() => {
    loadBugReports();
  }, [loadBugReports]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Zmień kierunek sortowania
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Ustaw nową kolumnę i domyślny kierunek
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const getSortedBugReports = (reports: BugReport[]): BugReport[] => {
    if (!sortColumn) return reports;
    
    const sorted = [...reports].sort((a, b) => {
      let aValue: any;
      let bValue: any;
      
      switch (sortColumn) {
        case 'id':
          aValue = a.id;
          bValue = b.id;
          break;
        case 'date':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'title':
          aValue = a.title?.toLowerCase() || '';
          bValue = b.title?.toLowerCase() || '';
          break;
        case 'reporter':
          aValue = (a.reporter_name || users.find(u => u.user_id === a.reporter_user_id)?.display_name || users.find(u => u.user_id === a.reporter_user_id)?.email || a.reporter_user_id)?.toLowerCase() || '';
          bValue = (b.reporter_name || users.find(u => u.user_id === b.reporter_user_id)?.display_name || users.find(u => u.user_id === b.reporter_user_id)?.email || b.reporter_user_id)?.toLowerCase() || '';
          break;
        case 'status':
          aValue = a.status;
          bValue = b.status;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  };

  const renderSortIcon = (column: string) => {
    if (sortColumn !== column) {
      return <UnfoldMore sx={{ fontSize: 16, color: '#B9B9B9', ml: 0.5 }} />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUpward sx={{ fontSize: 16, color: '#595966', ml: 0.5 }} />
      : <ArrowDownward sx={{ fontSize: 16, color: '#595966', ml: 0.5 }} />;
  };

  const renderStatusChip = (status: string) => {
    const text = '#020242';
    const map: Record<string, { bg: string; fg: string }> = {
      'Zgłoszono': { bg: '#E6F0FF', fg: text },
      'Przetestować': { bg: '#FEEED6', fg: text },
      'Rozwiązano': { bg: '#EAF7EA', fg: text },
      'Nie działa': { bg: '#FFE6E6', fg: text },
    };
    const palette = map[status] || { bg: '#F1F1F6', fg: text };
    return (
      <Chip
        label={status}
        size="small"
        sx={{
          bgcolor: palette.bg,
          color: palette.fg,
          borderRadius: '4px',
          minHeight: 28,
          height: 28,
          '& .MuiChip-label': {
            px: 1,
            py: 0,
            fontFamily: 'Roboto, sans-serif',
            fontSize: '14px',
            fontWeight: 400,
            lineHeight: '16px',
            color: palette.fg
          }
        }}
      />
    );
  };

  const handlePreview = async (bugReport: BugReport) => {
    try {
      // Pobierz świeże dane z API, aby mieć pewność że URL są poprawnie znormalizowane
      const freshReport = await bugReportService.getBugReport(bugReport.id);
      console.log('[BugReportPage] handlePreview - freshReport screenshots:', freshReport.screenshots);
      setSelectedBugReport(freshReport);
      setPreviewOpen(true);
      
      // Załaduj historię i komentarze
      setLoadingHistory(true);
      try {
        const [historyData, commentsData] = await Promise.all([
          bugReportService.getBugReportHistory(bugReport.id),
          bugReportService.getBugReportComments(bugReport.id)
        ]);
        setHistory(historyData);
        setComments(commentsData);
      } catch (err) {
        console.error('Błąd podczas ładowania historii/komentarzy:', err);
        setHistory([]);
        setComments([]);
      } finally {
        setLoadingHistory(false);
      }
    } catch (error) {
      console.error('Błąd podczas pobierania zgłoszenia:', error);
      // Fallback: użyj danych z listy (powinny być już znormalizowane)
      console.log('[BugReportPage] handlePreview - fallback bugReport screenshots:', bugReport.screenshots);
      setSelectedBugReport(bugReport);
      setPreviewOpen(true);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, bugReport: BugReport) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuRow(bugReport);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuRow(null);
  };

  const handleEdit = () => {
    if (menuRow) {
      setSelectedBugReport(menuRow);
      setEditOpen(true);
      setFormOpen(true);
    }
    handleMenuClose();
  };

  const handleStatusChange = async (newStatus: 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa') => {
    if (!selectedBugReport) return;
    try {
      setUpdatingStatus(true);
      const updated = await bugReportService.updateBugReportStatus(selectedBugReport.id, newStatus);
      setSelectedBugReport(updated);
      loadBugReports(); // Odśwież listę
      
      // Odśwież historię
      try {
        const historyData = await bugReportService.getBugReportHistory(selectedBugReport.id);
        setHistory(historyData);
      } catch (err) {
        console.error('Błąd podczas odświeżania historii:', err);
      }
    } catch (error: any) {
      console.error('Błąd podczas zmiany statusu:', error);
      alert(error.response?.data?.message || error.message || 'Nie udało się zmienić statusu');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleAddComment = async () => {
    if (!selectedBugReport || !newComment.trim()) return;
    try {
      setAddingComment(true);
      const newCommentData = await bugReportService.addBugReportComment(selectedBugReport.id, newComment.trim());
      setComments([...comments, newCommentData]);
      setNewComment('');
      
      // Odśwież historię
      try {
        const historyData = await bugReportService.getBugReportHistory(selectedBugReport.id);
        setHistory(historyData);
      } catch (err) {
        console.error('Błąd podczas odświeżania historii:', err);
      }
    } catch (error: any) {
      console.error('Błąd podczas dodawania komentarza:', error);
      alert(error.response?.data?.message || error.message || 'Nie udało się dodać komentarza');
    } finally {
      setAddingComment(false);
    }
  };

  const handleStatusChangeFromMenu = async (newStatus: 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa') => {
    if (!menuRow) return;
    try {
      await bugReportService.updateBugReportStatus(menuRow.id, newStatus);
      loadBugReports(); // Odśwież listę
    } catch (error: any) {
      console.error('Błąd podczas zmiany statusu:', error);
      alert(error.response?.data?.message || error.message || 'Nie udało się zmienić statusu');
    }
    handleMenuClose();
  };

  return (
    <Box sx={{ bgcolor: '#FAFAFA', mt: '20px' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mt: 0, mb: 2 }}>
        <Typography
          component="h1"
          sx={{
            fontFamily: 'Roboto, sans-serif',
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 400,
            color: '#020242',
            m: 0,
            mt: '8.5px'
          }}
        >
          Raportowanie błędów
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
          <Button 
            variant="contained" 
            onClick={() => setFormOpen(true)}
            sx={{ 
              width: 116,
              height: 40,
              borderRadius: '4px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              minWidth: 'auto',
              backgroundColor: '#4040DB',
              color: '#fff',
              '&:hover': { backgroundColor: '#1F1FB0' },
              '&:active': { backgroundColor: '#1A1A98' }
            }}
          >
            Dodaj błąd
          </Button>
        </Box>
      </Box>

      <Box 
        sx={{ 
          display: 'grid',
          gridTemplateColumns: '300px 300px 200px',
          columnGap: '24px',
          alignItems: 'start',
          height: 56,
          mb: 2,
          bgcolor: '#FAFAFA'
        }}
      >
        {/* Search 300x36, white bg, #d4d4d9 border, 20px icon */}
        <TextField
          size="small"
          placeholder="Szukaj błędu"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          variant="outlined"
          inputProps={{ 
            'data-role': 'page-filter',
            onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => { 
              if (e.key === 'Escape') { 
                e.preventDefault(); 
                e.stopPropagation(); 
                try { (e.currentTarget as HTMLInputElement).blur(); } catch {} 
              } 
            } 
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ width: 20, height: 20, color: '#595966' }} />
              </InputAdornment>
            )
          }}
          sx={{ 
            width: 300,
            mt: '20px',
            '& .MuiOutlinedInput-root': {
              height: 36,
              backgroundColor: '#fff',
              borderRadius: '4px'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d4d4d9'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966'
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#595966 !important'
            },
            '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' },
            '& .MuiInputBase-input': { px: '12px', fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' },
            '& .MuiInputBase-input::placeholder': { color: '#B9B9B9', opacity: 1 }
          }}
        />

        {/* Group: Zgłaszający + Select 300x36, label 16px height */}
        <Box sx={{ width: 300, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Zgłaszający</Typography>
          <Select
            size="small"
            value={manager}
            onChange={(e) => setManager(e.target.value)}
            displayEmpty
            renderValue={manager !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png"
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{ 
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex', alignItems: 'center', height: 36,
                fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: manager ? '#2D2D32' : '#B9B9B9'
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' },
              '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszyscy</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.user_id} value={u.display_name || u.email || u.user_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Avatar src={u.avatar_url || undefined} sx={{ width: 22, height: 22 }} />
                  <Box component="span">{u.display_name || u.email}</Box>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>

        {/* Group: Status + Select 200x36, label 16px height */}
        <Box sx={{ width: 200, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '16px', color: '#2D2D32' }}>Status</Typography>
          <Select
            size="small"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as '' | 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa')}
            displayEmpty
            renderValue={statusFilter !== '' ? undefined : () => 'Wybierz z listy'}
            IconComponent={(iconProps:any) => (
              <Box
                component="img"
                alt="dropdown"
                src="/assets/filters/keyboard_arrow_down.png"
                {...iconProps}
                sx={{ width: 18, height: 18, mr: '8px', pointerEvents: 'none' }}
              />
            )}
            sx={{ 
              height: 36,
              pr: '8px',
              '& .MuiSelect-select': {
                display: 'flex', alignItems: 'center', height: 36,
                fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: statusFilter ? '#2D2D32' : '#B9B9B9'
              },
              backgroundColor: '#fff',
              borderRadius: '4px',
              '& fieldset': { borderColor: '#d4d4d9' },
              '&:hover fieldset': { borderColor: '#595966' },
              '& .MuiOutlinedInput-notchedOutline': { transition: 'border-color 0.2s' },
              '&.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#595966 !important' },
              '& .MuiOutlinedInput-root.Mui-focused': { boxShadow: 'none', outline: 'none' }
            }}
          >
            <MenuItem value="">Wszystkie</MenuItem>
            <MenuItem value="Zgłoszono">Zgłoszono</MenuItem>
            <MenuItem value="Przetestować">Przetestować</MenuItem>
            <MenuItem value="Rozwiązano">Rozwiązano</MenuItem>
            <MenuItem value="Nie działa">Nie działa</MenuItem>
          </Select>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end', gap: '2px', height: 40, mt: '26.5px', mb: 0 }}>
        <IconButton
          size="small"
          sx={{
            width: 32,
            height: 32,
            borderRadius: '4px',
            mt: '4px',
            p: 0,
            alignSelf: 'flex-end',
            bgcolor: colsOpen ? '#E0E0F5' : 'transparent',
            transition: 'background-color 0.15s ease',
            '&:hover': { bgcolor: 'rgba(0,0,0,0.06)' }
          }}
          onClick={(e) => {
            setColResizeMode(true);
            setColsAnchorEl(e.currentTarget);
          }}
        >
          <Box component="img" alt="kolumny" src="/assets/icons/frame-344.svg?v=2" sx={{ width: 32, height: 32 }} />
        </IconButton>
      </Box>

      <TableContainer component={Paper} sx={{ height: tableHeight, overflow: 'auto', position: 'relative', border: '1px solid #d0d0d6', borderBottom: '1px solid #d0d0d6', borderRadius: 0, borderTopRightRadius: 4, borderBottomLeftRadius: 4, borderBottomRightRadius: 4, overflowX: 'hidden', overflowY: 'auto', boxShadow: 'none', scrollbarWidth: 'none', msOverflowStyle: 'none', '&::-webkit-scrollbar': { width: 0, height: 0 } }}>
          <Table size="small" stickyHeader sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': { padding: '1px 4px', fontSize: '0.85rem', borderRadius: 0 },
            '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: '#FAFAFA', borderRight: '1px solid #d4d4d9', borderRadius: 0, boxShadow: 'none' },
            '& .MuiTableCell-body': { borderRight: 'none', borderRadius: 0 },
            '& .MuiTableRow-head > *:last-child': { borderRight: 'none', borderTopRightRadius: 4 }
          }}>
            <TableHead>
              <TableRow>
                {(draftOrder ?? columnOrder).map((key) => {
                  if (!colV(key)) return null;
                  if (key === 'id') {
                    return renderResizableTh('id', 'ID', 'left', true);
                  }
                  if (key === 'date') {
                    return renderResizableTh('date', 'Data', 'left', true);
                  }
                  if (key === 'title') {
                    return renderResizableTh('title', 'Tytuł', 'left', true);
                  }
                  if (key === 'reporter') {
                    return renderResizableTh('reporter', 'Zgłaszający', 'left', true);
                  }
                  if (key === 'status') {
                    return renderResizableTh('status', 'Status', 'left', true);
                  }
                  if (key === 'preview') {
                    return renderResizableTh('preview', '', 'center', false);
                  }
                  if (key === 'actions') {
                    return renderResizableTh('actions', '', 'center', false);
                  }
                  return null;
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <>
                  {[1, 2, 3].map((i) => (
                    <TableRow key={`loading-${i}`}>
                      {(draftOrder ?? columnOrder).map((key) => {
                        if (!colV(key)) return null;
                        if (key === 'id') {
                          return <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('id'), display: colV('id') ? 'table-cell' : 'none' }}>
                            <Box sx={{ width: 40, height: 16, bgcolor: '#E0E0E0', borderRadius: '4px' }} />
                          </TableCell>;
                        }
                        if (key === 'date') {
                          return <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('date'), display: colV('date') ? 'table-cell' : 'none' }}>
                            <Box sx={{ width: 80, height: 16, bgcolor: '#E0E0E0', borderRadius: '4px' }} />
                          </TableCell>;
                        }
                        if (key === 'title') {
                          return <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('title'), display: colV('title') ? 'table-cell' : 'none' }}>
                            <Box sx={{ width: 200, height: 16, bgcolor: '#E0E0E0', borderRadius: '4px' }} />
                          </TableCell>;
                        }
                        if (key === 'reporter') {
                          return <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('reporter'), display: colV('reporter') ? 'table-cell' : 'none' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 22, height: 22, bgcolor: '#E0E0E0', borderRadius: '50%' }} />
                              <Box sx={{ width: 100, height: 16, bgcolor: '#E0E0E0', borderRadius: '4px' }} />
                            </Box>
                          </TableCell>;
                        }
                        if (key === 'status') {
                          return <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('status'), display: colV('status') ? 'table-cell' : 'none' }}>
                            <Box sx={{ width: 100, height: 24, bgcolor: '#E0E0E0', borderRadius: '16px' }} />
                          </TableCell>;
                        }
                        if (key === 'preview') {
                          return <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('preview'), display: colV('preview') ? 'table-cell' : 'none' }}>
                            <Box sx={{ width: 20, height: 20, bgcolor: '#E0E0E0', borderRadius: '4px' }} />
                          </TableCell>;
                        }
                        if (key === 'actions') {
                          return <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('actions'), display: colV('actions') ? 'table-cell' : 'none' }}>
                            <Box sx={{ width: 20, height: 20, bgcolor: '#E0E0E0', borderRadius: '4px' }} />
                          </TableCell>;
                        }
                        return null;
                      })}
                    </TableRow>
                  ))}
                </>
              ) : bugReports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={(draftOrder ?? columnOrder).filter(k => colV(k)).length} sx={{ textAlign: 'center', padding: '40px 20px' }}>
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#B9B9B9' }}>
                      Brak zgłoszeń
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                getSortedBugReports(bugReports).map((bug) => {
                  const reporter = users.find(u => u.user_id === bug.reporter_user_id);
                  return (
                    <TableRow key={bug.id} hover sx={{ '&:hover': { backgroundColor: '#F5F5FF !important' }, '&.MuiTableRow-hover:hover': { backgroundColor: '#F5F5FF !important' }, '&.Mui-selected': { backgroundColor: '#F5F5FF !important' }, '&.Mui-selected:hover': { backgroundColor: '#F5F5FF !important' } }}>
                      {(draftOrder ?? columnOrder).map((key) => {
                        if (!colV(key)) return null;
                        if (key === 'id') {
                          return <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '6px 12px', fontSize: '0.875rem', width: getColW('id'), display: colV('id') ? 'table-cell' : 'none' }}>{bug.id}</TableCell>;
                        }
                        if (key === 'date') {
                          return <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '6px 12px', fontSize: '0.875rem', width: getColW('date'), display: colV('date') ? 'table-cell' : 'none' }}>{formatDate(bug.created_at)}</TableCell>;
                        }
                        if (key === 'title') {
                          return <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '6px 12px', fontSize: '0.875rem', width: getColW('title'), display: colV('title') ? 'table-cell' : 'none' }}>{bug.title}</TableCell>;
                        }
                        if (key === 'reporter') {
                          return (
                            <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '6px 12px', fontSize: '0.875rem', width: getColW('reporter'), display: colV('reporter') ? 'table-cell' : 'none' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar src={reporter?.avatar_url || undefined} sx={{ width: 22, height: 22 }} />
                                <Box component="span">{bug.reporter_name || reporter?.display_name || reporter?.email || bug.reporter_user_id}</Box>
                              </Box>
                            </TableCell>
                          );
                        }
                        if (key === 'status') {
                          return <TableCell key={key} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '6px 12px', fontSize: '0.875rem', width: getColW('status'), display: colV('status') ? 'table-cell' : 'none' }}>{renderStatusChip(bug.status)}</TableCell>;
                        }
                        if (key === 'preview') {
                          return (
                            <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('preview'), display: colV('preview') ? 'table-cell' : 'none' }}>
                              <IconButton
                                size="small"
                                onClick={() => handlePreview(bug)}
                                sx={{ padding: '4px', color: '#595966' }}
                              >
                                <Visibility sx={{ width: 20, height: 20 }} />
                              </IconButton>
                            </TableCell>
                          );
                        }
                        if (key === 'actions') {
                          return (
                            <TableCell key={key} sx={{ padding: '6px 12px', fontSize: '0.875rem', width: getColW('actions'), display: colV('actions') ? 'table-cell' : 'none' }}>
                              <IconButton
                                size="small"
                                onClick={(e) => handleMenuOpen(e, bug)}
                                sx={{ padding: '4px', color: '#595966' }}
                              >
                                <MoreVert sx={{ width: 20, height: 20 }} />
                              </IconButton>
                            </TableCell>
                          );
                        }
                        return null;
                      })}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>


      {/* Formularz zgłaszania błędu */}
      <AddBugReportForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditOpen(false);
          setSelectedBugReport(null);
        }}
        onAdd={async () => {
          // Resetuj stan edycji przed odświeżeniem
          const wasEditing = editOpen && selectedBugReport;
          const editedId = wasEditing ? selectedBugReport.id : null;
          setEditOpen(false);
          setSelectedBugReport(null);
          const reports = await loadBugReports();
          // Jeśli edytowaliśmy zgłoszenie i był otwarty podgląd, odśwież go
          if (wasEditing && editedId) {
            const updated = reports.find(b => b.id === editedId);
            if (updated) {
              setSelectedBugReport(updated);
              setPreviewOpen(true);
            }
          }
        }}
        onDelete={(id) => {
          // Optymistyczne usunięcie - natychmiast usuń z listy
          setBugReports(prev => prev.filter(bug => bug.id !== id));
          // Resetuj stan edycji jeśli usuwany rekord był edytowany
          if (selectedBugReport?.id === id) {
            setSelectedBugReport(null);
            setEditOpen(false);
          }
        }}
        editData={editOpen ? selectedBugReport : null}
      />

      {/* Menu akcji */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEdit}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edytuj</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleStatusChangeFromMenu('Przetestować')}>
          <ListItemText>Status: Przetestować</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleStatusChangeFromMenu('Rozwiązano')}>
          <ListItemText>Status: Rozwiązano</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleStatusChangeFromMenu('Nie działa')}>
          <ListItemText>Status: Nie działa</ListItemText>
        </MenuItem>
      </Menu>

      {/* Okno „Dostosuj kolumny" */}
      <Backdrop open={colsOpen} sx={{ color: '#fff', zIndex: (theme: any) => theme.zIndex.modal + 1, backgroundColor: 'rgba(0,0,0,0.35)', pointerEvents: 'none' }}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 0,
            right: 0,
            height: '50vh',
            width: '280px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '0px 5px 30px rgba(0,0,0,0.15)',
            borderRadius: 0,
            zIndex: (theme: any) => theme.zIndex.modal + 2,
            backgroundColor: '#fff',
            pointerEvents: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flex: '0 0 auto', position: 'sticky', top: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: 16, color: '#2D2D32' }}>Dostosuj kolumny</Typography>
            <IconButton size="small" onClick={() => { setColsAnchorEl(null); setDraftColumnsVisible(null); setDraftOrder(null); setColResizeMode(false); }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
          <Divider />
          <Box sx={{ px: 0, py: 1.5, flex: '1 1 auto', minHeight: 0, overflowY: 'auto' }}>
            {(draftOrder ?? columnOrder).map((key) => {
              const list = draftColumnsVisible ?? columnsVisible;
              const ord = draftOrder ?? columnOrder;
              return (
                <Box
                  key={key}
                  sx={{ py: 1, px: 2 }}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/plain', key); }}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const src = e.dataTransfer.getData('text/plain');
                    if (!src || src === key) return;
                    setDraftOrder((prev) => {
                      const cur = [...(prev || ord)];
                      const from = cur.indexOf(src);
                      const to = cur.indexOf(key);
                      if (from < 0 || to < 0) return cur;
                      cur.splice(to, 0, cur.splice(from, 1)[0]);
                      return cur;
                    });
                  }}
                >
                  <Box sx={{ border: '1px solid #d4d4d9', borderRadius: '4px', height: 36, width: '100%', maxWidth: 257, display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                      <IconButton onClick={() => setDraftColumnsVisible((prev) => ({ ...(prev || list), [key]: !((prev || list)[key] !== false) }))} size="small" sx={{ p: 0 }} aria-label="toggle column">
                        <Box component="img" src={(draftColumnsVisible ?? columnsVisible)[key] !== false ? '/assets/icons/checkbox-on.svg' : '/assets/icons/checkbox-off.svg'} alt="check" sx={{ width: 23, height: 24 }} />
                      </IconButton>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: 14, color: '#2D2D32' }}>
                        {key === 'id' ? 'ID' :
                         key === 'date' ? 'DATA' :
                         key === 'title' ? 'TYTUŁ' :
                         key === 'reporter' ? 'ZGŁASZAJĄCY' :
                         key === 'status' ? 'STATUS' :
                         key === 'preview' ? 'PODGLĄD' :
                         key === 'actions' ? 'AKCJE' : key.toUpperCase()}
                      </Typography>
                    </Box>
                    <DragIndicator fontSize="small" sx={{ color: '#595966', cursor: 'grab' }} />
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Divider />
          <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '24px', flex: '0 0 auto', boxShadow: '0px 4px 4px rgba(0,0,0,0.05)', position: 'sticky', bottom: 0, bgcolor: '#FFFFFF', zIndex: 1 }}>
            <Button variant="text" onClick={() => { setColsAnchorEl(null); setDraftColumnsVisible(null); setDraftOrder(null); setColResizeMode(false); }} sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: 14, color: '#2D2D32', textTransform: 'none' }}>Anuluj</Button>
            <Button
              variant="contained"
              sx={{ width: 123, height: 40, borderRadius: '4px', textTransform: 'none', fontFamily: 'Roboto, sans-serif', fontWeight: 600, fontSize: 14, backgroundColor: '#020242', '&:hover': { backgroundColor: '#020242' } }}
              onClick={() => {
                const vis = draftColumnsVisible ?? columnsVisible;
                setColumnsVisible(vis);
                try { localStorage.setItem(VISIBLE_KEY, JSON.stringify(vis)); } catch {}
                if (draftOrder) {
                  setColumnOrder(draftOrder);
                  try { localStorage.setItem(ORDER_KEY, JSON.stringify(draftOrder)); } catch {}
                }
                setColsAnchorEl(null);
                setDraftColumnsVisible(null);
                setDraftOrder(null);
                setColResizeMode(false);
              }}
            >
              Zapisz zmiany
            </Button>
          </Box>
        </Paper>
      </Backdrop>

      {/* Drawer podglądu zgłoszenia */}
      <Drawer
        anchor="right"
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setSelectedBugReport(null);
          setHistory([]);
          setComments([]);
          setNewComment('');
        }}
        PaperProps={{ sx: { width: 680, maxWidth: '100vw', borderRadius: 0 } }}
      >
        {selectedBugReport && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Nagłówek */}
            <Box sx={{ pl: 4, pr: 4, pt: '22px', pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 500, fontSize: '18px', color: '#2D2D32' }}>
                {selectedBugReport.title}
              </Typography>
              <IconButton 
                onClick={() => {
                  setPreviewOpen(false);
                  setSelectedBugReport(null);
                }} 
                size="small"
                sx={{
                  position: 'relative',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 30,
                  minHeight: 30,
                  '& > *': {
                    pointerEvents: 'none',
                  },
                }}
              >
                <Box 
                  component="img" 
                  src="/assets/close.svg" 
                  alt="Zamknij" 
                  sx={{ 
                    width: 13, 
                    height: 13, 
                    display: 'block',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                  }} 
                />
              </IconButton>
            </Box>

            {/* Zawartość */}
            <Box sx={{ flex: 1, overflow: 'auto', px: 4, py: 3 }}>
              <Grid container rowSpacing={1.5} columnSpacing={3}>
                {/* Status */}
                <Grid item xs={12}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
                    Status
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {updatingStatus && <CircularProgress size={16} />}
                    <Select
                      value={selectedBugReport.status}
                      onChange={(e) => handleStatusChange(e.target.value as 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa')}
                      disabled={updatingStatus}
                      size="small"
                      sx={{
                        minWidth: 200,
                        height: 36,
                        backgroundColor: '#fff',
                        borderRadius: '4px',
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#d4d4d9'
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#595966'
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#595966 !important'
                        },
                        '& .MuiSelect-select': {
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '14px',
                          color: '#2D2D32',
                          py: '8px',
                          display: 'flex',
                          alignItems: 'center'
                        }
                      }}
                      renderValue={(value) => renderStatusChip(value)}
                    >
                      <MenuItem value="Zgłoszono">
                        {renderStatusChip('Zgłoszono')}
                      </MenuItem>
                      <MenuItem value="Przetestować">
                        {renderStatusChip('Przetestować')}
                      </MenuItem>
                      <MenuItem value="Rozwiązano">
                        {renderStatusChip('Rozwiązano')}
                      </MenuItem>
                      <MenuItem value="Nie działa">
                        {renderStatusChip('Nie działa')}
                      </MenuItem>
                    </Select>
                  </Box>
                </Grid>

                {/* Data zgłoszenia */}
                <Grid item xs={12}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
                    Data zgłoszenia
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', color: '#2D2D32' }}>
                    {formatDate(selectedBugReport.created_at)}
                  </Typography>
                </Grid>

                {/* Zgłaszający */}
                <Grid item xs={12}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
                    Zgłaszający
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', color: '#2D2D32' }}>
                    {selectedBugReport.reporter_name || users.find(u => u.user_id === selectedBugReport.reporter_user_id)?.display_name || selectedBugReport.reporter_user_id}
                  </Typography>
                </Grid>

                {/* Opis */}
                <Grid item xs={12}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
                    Opis
                  </Typography>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '16px', color: '#2D2D32', whiteSpace: 'pre-wrap' }}>
                    {selectedBugReport.description}
                  </Typography>
                </Grid>

                {/* Screeny */}
                {(() => {
                  const screenshots = selectedBugReport.screenshots || [];
                  console.log('[BugReportPage] Render screenshots:', screenshots, 'length:', screenshots.length, 'selectedBugReport:', selectedBugReport);
                  if (screenshots.length === 0) return null;
                  return (
                    <Grid item xs={12}>
                      <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 0.5 }}>
                        Screeny
                      </Typography>
                      <Box sx={{ display: 'grid', 
                        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' },
                        gap: 2 }}>
                        {screenshots.map((url, index) => {
                          console.log('[BugReportPage] Rendering screenshot:', index, url);
                          return (
                            <Box
                              key={index}
                              component="img"
                              src={url}
                              alt={`Screenshot ${index + 1}`}
                              onClick={() => setEnlargedImageUrl(url)}
                              onError={(e) => {
                                console.error('Błąd ładowania obrazu:', url, e);
                              }}
                              sx={{
                                width: '100%',
                                aspectRatio: '1',
                                objectFit: 'cover',
                                borderRadius: '4px',
                                border: '1px solid #d4d4d9',
                                cursor: 'pointer',
                                '&:hover': {
                                  borderColor: '#595966',
                                  opacity: 0.9
                                }
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Grid>
                  );
                })()}

                {/* Historia */}
                <Grid item xs={12}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 1 }}>
                    Historia
                  </Typography>
                  {loadingHistory ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                      <CircularProgress size={20} />
                    </Box>
                  ) : history.length === 0 ? (
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#B9B9B9' }}>
                      Brak historii
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {history.map((item) => (
                        <Box key={item.id} sx={{ p: 1.5, bgcolor: '#FAFAFA', borderRadius: '4px', border: '1px solid #E0E0E0' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#2D2D32' }}>
                              {item.user_name || item.user_email || item.user_id}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#B9B9B9' }}>
                              {formatDate(item.created_at)}
                            </Typography>
                          </Box>
                          {item.action_type === 'created' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>
                                Zgłosił błąd, status:
                              </Typography>
                              {renderStatusChip(item.new_status || 'Zgłoszono')}
                            </Box>
                          )}
                          {item.action_type === 'status_changed' && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32' }}>
                                Zmienił status: {item.old_status} →
                              </Typography>
                              {renderStatusChip(item.new_status || '')}
                            </Box>
                          )}
                          {item.action_type === 'comment_added' && (
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32', whiteSpace: 'pre-wrap' }}>
                              Dodał komentarz: {item.comment_text}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Grid>

                {/* Komentarze */}
                <Grid item xs={12}>
                  <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontWeight: 400, fontSize: '14px', lineHeight: '20px', color: '#2D2D32', mb: 1 }}>
                    Komentarze
                  </Typography>
                  
                  {/* Lista komentarzy */}
                  {comments.length === 0 ? (
                    <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#B9B9B9', mb: 2 }}>
                      Brak komentarzy
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                      {comments.map((comment) => (
                        <Box key={comment.id} sx={{ p: 1.5, bgcolor: '#FAFAFA', borderRadius: '4px', border: '1px solid #E0E0E0' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Avatar src={comment.user_avatar_url || undefined} sx={{ width: 24, height: 24 }} />
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', fontWeight: 500, color: '#2D2D32' }}>
                              {comment.user_name || comment.user_email || comment.user_id}
                            </Typography>
                            <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '12px', color: '#B9B9B9' }}>
                              {formatDate(comment.created_at)}
                            </Typography>
                          </Box>
                          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '14px', color: '#2D2D32', whiteSpace: 'pre-wrap' }}>
                            {comment.comment_text}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Formularz dodawania komentarza */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                      multiline
                      rows={3}
                      placeholder="Dodaj komentarz..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      disabled={addingComment}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          borderRadius: '4px',
                          '& fieldset': {
                            borderColor: '#d4d4d9'
                          },
                          '&:hover fieldset': {
                            borderColor: '#595966'
                          },
                          '&.Mui-focused fieldset': {
                            borderColor: '#595966 !important'
                          }
                        },
                        '& .MuiInputBase-input': {
                          fontFamily: 'Roboto, sans-serif',
                          fontSize: '14px',
                          color: '#2D2D32'
                        }
                      }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        variant="contained"
                        onClick={handleAddComment}
                        disabled={addingComment || !newComment.trim()}
                        sx={{
                          minWidth: 120,
                          height: 36,
                          borderRadius: '4px',
                          textTransform: 'none',
                          fontSize: '14px',
                          fontWeight: 600,
                          backgroundColor: '#4040DB',
                          color: '#fff',
                          '&:hover': { backgroundColor: '#1F1FB0' },
                          '&:disabled': { backgroundColor: '#d4d4d9', color: '#fff' }
                        }}
                      >
                        {addingComment ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Dodaj komentarz'}
                      </Button>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* Dialog powiększonego zdjęcia */}
      <Dialog
        open={!!enlargedImageUrl}
        onClose={() => setEnlargedImageUrl(null)}
        fullScreen
        sx={{ zIndex: 2147484005 }}
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.9)' } }}
        PaperProps={{ sx: { backgroundColor: 'black' } }}
      >
        <Box
          sx={{ p: 0, bgcolor: 'black', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}
          onClick={() => setEnlargedImageUrl(null)}
        >
          {enlargedImageUrl && (
            <Box component="img" src={enlargedImageUrl} alt="Powiększone zdjęcie" sx={{ maxWidth: '100vw', maxHeight: '100vh', width: 'auto', height: 'auto' }} />
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default BugReportPage;
